import React, { useEffect, useState } from 'react';
import {Table, Space, Button, message, Select} from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import axios from 'axios';
import TransferProjectWizard from '../transferProjectProjectViews/TransferProjectWizard';
import { getConfig } from "../../../../../config";
import TransferProductMain from "../transferProjectProductViews/TransferProductMain";
import TransferOrderCardMain from "../transferOrderCardMain/TransferOrderCardMain";
import './TransferProjectView.css';

interface ProjectData {
    id: number;
    name: string;
    customer: string;
    location: string;
    projectDone: boolean;
    productDone: boolean;
    orderCardDone: boolean;
}

interface ProjectManager {
    projectManagerNumber: string;
    firstName: string;
    secondName: string;
    projectNo: string;
    name: string;
}

const { API_BASE_URL } = getConfig();

const TransferProjectView: React.FC = () => {
    const [data, setData] = useState<ProjectData[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isWizardVisible, setIsWizardVisible] = useState(false);
    const [selectedProject, setSelectedProject] = useState<string | null>(null);
    const [selectedWizardType, setSelectedWizardType] = useState<"project" | "product" | "orderCard" | null>(null);
    const [projectManagers, setProjectManagers] = useState<ProjectManager[]>([]);
    const [selectedProjectManager, setSelectedProjectManager] = useState<string | null>(null);
    const [selectedProjectManagerNo, setSelectedProjectManagerNo] = useState<number | null>(null);

    const fetchProjectManagers = async () => {
        try {
            const response = await axios.get<ProjectManager[]>(`${API_BASE_URL}/getProjectManagerManagers`);
            setProjectManagers(response.data);
        } catch (error) {
            message.error("Fehler beim Laden der Projektleiter.");
        }
    };

    const fetchData = async () => {
        setIsLoading(true);
        try {
            const response = await axios.get<ProjectData[]>(
                `${API_BASE_URL}/getLegacyProjectData?auth=VdjCKidks7qTEyuU6QeJtezZ3PeCHhZQ2yHJ3kPtcfTN8BjVqU${selectedProjectManager ? `&projectManagerId=${selectedProjectManagerNo}` : ''}`
            );
            setData(response.data);
        } catch (error) {
            console.error('Error fetching data:', error);
            message.error('Fehler beim Laden der Projektdaten.');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [selectedProjectManager]);

    useEffect(() => {
        fetchProjectManagers();
        const savedManager = localStorage.getItem("selectedProjectManager");
        if (savedManager) {
            setSelectedProjectManager(savedManager);
            const savedManagerNo = localStorage.getItem("selectedProjectManagerNo");
            setSelectedProjectManagerNo(savedManagerNo !== null ? Number(savedManagerNo) : null);
        }
    }, []);


    const handleOpenWizard = (record: ProjectData, projectId: string, type: "project" | "product" | "orderCard") => {
        if (record.projectDone && type === "project")   {
            message.error('Projekt-Konfiguration wurde bereits abgeschlossen!');
            return
        }
        if (record.productDone && type === "product")   {
            message.error('Produkt-Konfiguration wurde bereits abgeschlossen!');
            return
        }
        if (record.orderCardDone && type === "orderCard")   {
            message.error('Auftragskarten-Konfiguration wurde bereits abgeschlossen!');
            return
        }
        setSelectedProject(projectId);
        setSelectedWizardType(type);
        setIsWizardVisible(true);
    };

    const handleWizardCompletion = () => {
        setIsWizardVisible(false);
        setSelectedWizardType(null);
        fetchData();
    };

    const renderBoolean = (value: boolean, record: ProjectData, type: "project" | "product" | "orderCard") => (
        <span
            onClick={() => handleOpenWizard(record, record.id.toString(), type)}
            className={`status-icon ${value ? "success" : "failed"}`}
        >
        {value ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
    </span>
    );


    const columns: ColumnsType<ProjectData> = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 100
        },
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            width: 200
        },
        {
            title: 'Kunde',
            dataIndex: 'customer',
            key: 'customer',
            width: 200
        },
        {
            title: 'Projekt Status',
            dataIndex: 'projectDone',
            key: 'projectDone',
            render: (value, record) => renderBoolean(value, record, "project"),
            width: 80
        },
        {
            title: 'Produkt Status',
            dataIndex: 'productDone',
            key: 'productDone',
            render: (value, record) => renderBoolean(value, record, "product"),
            width: 80
        },
        {
            title: 'AK Status',
            dataIndex: 'orderCardDone',
            key: 'orderCardDone',
            render: (value, record) => renderBoolean(value, record, "orderCard"),
            width: 80
        },
    ];

    return (
        <div style={{ padding: '20px' }}>
            <Space style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
                <Select
                    placeholder="Projektleiter auswählen"
                    style={{ width: 250 }}
                    allowClear
                    value={selectedProjectManager || undefined}
                    onChange={(value) => {
                        setSelectedProjectManager(value ?? null);
                        setSelectedProjectManagerNo(value ? Number(value) : null);
                    }}
                >
                    <Select.Option key="999" value="999">Alle</Select.Option>
                    {projectManagers.map(pm => (
                        <Select.Option key={pm.projectManagerNumber} value={pm.projectManagerNumber}>
                            {pm.firstName} {pm.secondName}
                        </Select.Option>
                    ))}
                </Select>

                <Button type="primary" onClick={fetchData} loading={isLoading}>
                    Aktualisieren
                </Button>
            </Space>
            <Table columns={columns} dataSource={data} rowKey="id" pagination={{ pageSize: 100 }} loading={isLoading} />

            {selectedProject && selectedWizardType === "project" && (
                <TransferProjectWizard
                    visible={isWizardVisible}
                    onClose={() => setIsWizardVisible(false)}
                    onCompletion={handleWizardCompletion}
                    selectedProject={selectedProject}
                />
            )}

            {selectedProject && selectedWizardType === "product" && (
                <TransferProductMain
                    visible={isWizardVisible}
                    onClose={() => setIsWizardVisible(false)}
                    onCompletion={handleWizardCompletion}
                    selectedProject={selectedProject}
                />
            )}
            {selectedProject && selectedWizardType === "orderCard" && (
                <TransferOrderCardMain
                    visible={isWizardVisible}
                    onClose={() => setIsWizardVisible(false)}
                    onCompletion={handleWizardCompletion}
                    selectedProject={selectedProject}
                />
            )}
        </div>
    );
};

export default TransferProjectView;
